import { audioStore } from "~/components/audio/audioStore";
import { SITE_CONFIG } from "~/lib/constants";

class MediaSessionService {
  private unsub: (() => void) | null = null;

  init() {
    if (typeof window === "undefined" || !("mediaSession" in navigator)) return;

    // Subscribe to audio store changes and reflect them into Media Session
    this.unsub = audioStore.subscribe((s) => {
      // Playback state
      navigator.mediaSession.playbackState = s.isPlaying
        ? "playing"
        : s.currentSong
          ? "paused"
          : "none";

      // Position state (if supported) — clamp position to avoid exceeding duration
      const msDuration = s.duration || 0;
      const msPosition = Math.min(s.currentTime || 0, msDuration);
      navigator.mediaSession.setPositionState({
        duration: msDuration,
        position: msPosition,
        playbackRate: 1,
      });

      // Metadata
      if (s.currentSong) {
        navigator.mediaSession.metadata = new window.MediaMetadata({
          title: s.currentSong.title || "",
          artist: SITE_CONFIG.name,
          artwork: [
            {
              src: s.currentSong.artworkSrc || "/logo.png",
              sizes: "512x512",
              type: "image/jpeg",
            },
          ],
        });
      } else {
        navigator.mediaSession.metadata = null;
      }
    });

    // Wire action handlers once
    navigator.mediaSession.setActionHandler("play", () => {
      audioStore.getState().resume();
    });
    navigator.mediaSession.setActionHandler("pause", () => {
      audioStore.getState().pause();
    });
    navigator.mediaSession.setActionHandler("seekbackward", (details) => {
      const skip = details?.seekOffset ?? 10;
      audioStore.getState().skip(-skip);
    });
    navigator.mediaSession.setActionHandler("seekforward", (details) => {
      const skip = details?.seekOffset ?? 10;
      audioStore.getState().skip(skip);
    });
    navigator.mediaSession.setActionHandler("seekto", (details) => {
      if (typeof details?.seekTime === "number")
        audioStore.getState().seek(details.seekTime);
    });
    // Prev/Next should operate on the store so playlist behaviour is correct
    navigator.mediaSession.setActionHandler("previoustrack", () => {
      audioStore.getState().prev();
    });
    navigator.mediaSession.setActionHandler("nexttrack", () => {
      audioStore.getState().next();
    });
  }

  dispose() {
    if (this.unsub) {
      this.unsub();
      this.unsub = null;
    }

    const actions: MediaSessionAction[] = [
      "play",
      "pause",
      "seekbackward",
      "seekforward",
      "previoustrack",
      "nexttrack",
      "seekto",
    ];
    for (const a of actions) navigator.mediaSession.setActionHandler(a, null);
    navigator.mediaSession.metadata = null;
  }
}

export const mediaSession = new MediaSessionService();
